import {
  type NavigationGuardNext,
  type RouteRecordRaw,
  createRouter,
  createWebHashHistory,
} from 'vue-router'
import dayjs from 'dayjs'

import DefaultLayout from '../layout/default/index.vue'
import Home from '@/views/index.vue'
import HomeLayout from '../layout/home/index.vue'
import Dashboard from '@/views/dashboard/index.vue'
import ReSetPassword from '@/views/password/index.vue'
import axios from 'axios'
import { loginPwdQuery } from '@mp/api/sdpSecurity/LoginPwdController.js'
import NotFound from '@/views/404.vue'
import type { AppRouteRecordRaw } from '@mp/types/router'
import NoPermission from '@/views/noPermission.vue'
import { operatorPermissionQuery } from '@mp/api/sdpMerchant/OperatorController.js'
import { toOldB } from '@mp/hooks'
import { mchCrossCheck } from '@mp/api/sdpMerchant/MerchantController.ts'

export type ConstantRoutes = AppRouteRecordRaw & {
  hidden?: boolean
  name?: string
  meta?: {
    keepAlive?: boolean
  }
  children?: Array<ConstantRoutes>
}

export const constantRoutes: Array<AppRouteRecordRaw> = [
  {
    path: '/',
    name: 'Home',
    meta: {
      title: '商户平台',
      hidden: true,
    },
    component: HomeLayout,
    children: [{
      name: 'HomePageNew',
      path: '/new',
      redirect: '/',
    }, {
      name: 'HomePage',
      path: '',
      meta: {
        title: '商户平台',
      },
      component: Home,

      // 判断是否已登录
      beforeEnter: async (_to, _from, next) => {
        const { data } = await axios.get('/mchgateway/user/info')
        if (data?.userInfo && data?.userInfo.loginAccount) {
          sessionStorage.setItem('operatorId', data.userInfo?.operatorId)
          next({ name: 'DashboardIndex' })
        }
        else { next() }
      },
    }],
  },
  {
    name: 'Dashboard',
    path: '/dashboard',
    meta: {
      title: '首页',
    },
    component: DefaultLayout,
    children: [{
      path: '',
      name: 'DashboardIndex',
      component: Dashboard,
      meta: {
        title: '首页',
      },
    }],
    beforeEnter: async (_to, _from, next: NavigationGuardNext) => {
      // ? 是否是跨境商户
      const { data: isCrossMch } = await mchCrossCheck()
      if (isCrossMch)
        return toOldB()

      // ? 查询是否需要修改密码
      const { resultCode, data } = await loginPwdQuery()
      if (resultCode !== 'SUCCESS' || !data)
        return next()

      if (data?.createTime === data?.updateTime)
        return next({ name: 'ResetPassword', query: { first: 1 } })

      const diff = dayjs().diff(data?.updateTime, 'day')
      if (diff > 90)
        return next({ name: 'ResetPassword' })
      next()
    },
  },
  {
    name: 'NoPermission',
    path: '/no-permission',
    meta: {
      title: '访问受限',
      hidden: true,
    },
    component: DefaultLayout,
    children: [{
      path: '',
      name: 'NoPermission',
      component: NoPermission,
    }],
  },
  {
    name: 'Reset',
    path: '/reset/password',
    meta: {
      title: '重置',
      hidden: true,
    },
    component: HomeLayout,
    children: [{
      path: '/reset/password',
      name: 'ResetPassword',
      component: ReSetPassword,
    }],
  },
  {
    path: '/:pathMatch(.*)*',
    name: 'NotFound',
    meta: {
      title: '404',
      hidden: true,
    },
    component: NotFound,
  },
]

// *动态导入其他路由
const modules = import.meta.glob('./modules/**/*.ts', { eager: true })
Object.values(modules).forEach((item: any) => constantRoutes.push(...item.default))

// createWebHashHistory
const router = createRouter({
  history: createWebHashHistory(),
  routes: constantRoutes as RouteRecordRaw[],
})

// *全局路由守卫
router.beforeEach(async (to, from, next) => {
  const { data } = await operatorPermissionQuery({
    customOptions: {
      errorMessageMode: 'none',
    },
  })
  const permissions = data?.permissions ?? []
  sessionStorage.setItem('permissions', JSON.stringify(permissions))

  // 白名单路由，不需要权限验证
  const whiteList = ['HomePage', 'NoPermission', 'ResetPassword', 'NotFound']

  if (whiteList.includes(to.name as string))
    return next()

  if (!to.meta?.permission)
    return next()

  // 检查目标路由是否需要权限
  if (to.meta?.permission) {
    const requiredPermissions = to.meta.permission as string[]

    // 检查用户是否拥有所需权限
    const hasPermission = requiredPermissions.some(permission =>
      permissions?.includes(permission),
    )

    if (!hasPermission)
      return next({ path: '/no-permission', replace: true })
  }

  next()
})

export default router
